import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { getAssets } from "../../services/digitalWalletService";
import {
  extractWalletAssetsListFromResponse,
  getBusinessBank,
  getBusinessBanking,
  getBusinessPortfolioEstimate,
  getBusinessPortfolioPerformance,
  getBusinessWalletAssets,
  getBusinessWalletTransactions,
  unwrapBusinessResponse,
} from "../../services/businessWalletService";
import { extractBusinessPerformancePayload } from "../../utils/businessPerformanceMappers";
import { normalizeBusinessWalletTransaction } from "../../utils/businessWalletTransaction";

export const businessWalletQueryKey = (businessId: string) =>
  ["businessWallet", businessId] as const;

type UsableAssetRow = {
  id: string;
  asset: string;
  assetName: string;
  image: string;
  availableAmount: number;
  formattedAvailableAmount?: string;
  valueInUsdt: number;
  valueInZar: number;
  valueInLocalCurrency?: string;
  currencySymbol?: string;
  currencyType?: string;
  type?: string;
  assetEstimatedPrice?: string | number;
  buyRate?: number;
  sellRate?: number;
  networks?: unknown;
  _businessRaw?: unknown;
};

function num(v: unknown): number {
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function mapBusinessApiAssetToUsable(
  raw: Record<string, unknown>,
  imageFallback: Map<string, string>
): UsableAssetRow {
  const symbol = String(
    raw.assetSymbol ?? raw.assetName ?? raw.asset ?? ""
  ).toUpperCase();
  const name = String(raw.assetName ?? symbol);
  const icon = raw.assetIcon
    ? String(raw.assetIcon)
    : imageFallback.get(symbol) || "";
  const balance = num(raw.availableBalance);
  const fiatEq = num(raw.fiatEquivalent);
  const formattedAvailableFromApi =
    typeof raw.formattedAvailableAmount === "string"
      ? raw.formattedAvailableAmount
      : typeof raw.forattedAvailableAmount === "string"
        ? raw.forattedAvailableAmount
        : undefined;

  const networks = raw.networks;
  const firstNet = Array.isArray(networks) ? networks[0] : null;
  const netId =
    firstNet &&
    typeof firstNet === "object" &&
    (firstNet as { assetId?: string }).assetId
      ? String((firstNet as { assetId: string }).assetId)
      : symbol;

  return {
    id: netId,
    asset: symbol,
    assetName: name,
    image: icon,
    availableAmount: balance,
    ...(formattedAvailableFromApi != null
      ? { formattedAvailableAmount: formattedAvailableFromApi }
      : {}),
    valueInUsdt: 0,
    valueInZar: fiatEq,
    /** Used by CryptoAssetsDashboard “Price” column; mirrors personal wallet `assetEstimatedPrice`. */
    assetEstimatedPrice: fiatEq,
    currencyType: "Crypto",
    type: "Crypto",
    networks,
    _businessRaw: raw,
  };
}

export function useBusinessPortfolioEstimate(
  businessId: string | null
): UseQueryResult<{
  usdtEstimate: number;
  zarEstimate: number;
  portfolioEstimate: string;
  portfolioEstimateRaw: number;
  totalDepositInZar: number;
  totalWithdrawInZar: number;
  assets: unknown[];
  timestamp?: string | number;
}> {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: businessId
      ? [...businessWalletQueryKey(businessId), "estimate"]
      : ["businessWallet", "none", "estimate"],
    queryFn: async () => {
      if (!businessId) {
        throw new Error("businessId required");
      }
      const res = await getBusinessPortfolioEstimate(businessId);
      const envelope = res?.data as Record<string, unknown> | undefined;
      const data = (envelope?.data ?? envelope) as Record<string, unknown>;
      const rawAssets = Array.isArray(data?.assets) ? data.assets : [];
      const assets = rawAssets.map((a) => {
        const o = a as Record<string, unknown>;
        return {
          ...o,
          type: o.type ?? "Crypto",
          currencyType: o.currencyType ?? "Crypto",
        };
      });
      return {
        usdtEstimate: num(data?.usdtEstimate),
        zarEstimate: num(data?.zarEstimate),
        portfolioEstimate: String(data?.portfolioEstimate ?? "0.00"),
        portfolioEstimateRaw: num(data?.portfolioEstimateRaw),
        totalDepositInZar: num(data?.totalDepositInZar),
        totalWithdrawInZar: num(data?.totalWithdrawInZar),
        assets,
        timestamp: data?.timestamp as string | number | undefined,
      };
    },
    enabled: Boolean(businessId) && isAuthenticated,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Same shape as `useWalletAssets` for business-scoped portfolio (v2).
 */
export function useBusinessWalletAssetsData(businessId: string | null) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: businessId
      ? [...businessWalletQueryKey(businessId), "usableAssets"]
      : ["businessWallet", "none", "usableAssets"],
    queryFn: async () => {
      if (!businessId) {
        throw new Error("businessId required");
      }
      const [estRes, walletRes] = await Promise.all([
        getBusinessPortfolioEstimate(businessId),
        getBusinessWalletAssets(businessId, { includeZeroBalances: false }),
      ]);
      const estEnv = estRes?.data as Record<string, unknown> | undefined;
      const estData = (estEnv?.data ?? estEnv) as
        | Record<string, unknown>
        | undefined;
      const rawList = extractWalletAssetsListFromResponse(
        walletRes as { data?: unknown }
      ) as Record<string, unknown>[];

      const assetsMetadata = await getAssets();
      const assetsMetadataList = assetsMetadata?.data?.data || [];
      const assetImageMap = new Map<string, string>();
      assetsMetadataList.forEach(
        (asset: { asset?: string; image?: string }) => {
          if (asset.asset && asset.image) {
            assetImageMap.set(asset.asset.toUpperCase(), asset.image);
          }
        }
      );

      const assetsForUse: UsableAssetRow[] = rawList.map((row) => {
        const u = mapBusinessApiAssetToUsable(row, assetImageMap);
        const key = u.asset?.toUpperCase();
        if (!u.image && key) {
          u.image = assetImageMap.get(key) || "";
        }
        return u;
      });

      return {
        assets: assetsForUse,
        usdtEstimate: num(estData?.usdtEstimate),
        zarEstimate: num(estData?.zarEstimate),
        portfolioEstimate: String(estData?.portfolioEstimate ?? "0.00"),
        portfolioEstimateRaw: num(estData?.portfolioEstimateRaw),
        timestamp: estData?.timestamp as string | number | undefined,
      };
    },
    enabled: Boolean(businessId) && isAuthenticated,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Portfolio-based available assets (mirrors `useAvailableAssets` for business).
 */
export function useBusinessAvailableAssets({
  businessId,
  isSend,
  isEightyeightPay,
  balanceParams,
  balanceParamsEightEight,
  investArray: _investArray,
  enabled = true,
}: {
  businessId: string | null;
  isSend: boolean;
  isEightyeightPay: boolean;
  balanceParams?: { type?: string; id?: string };
  balanceParamsEightEight?: { id?: string; type?: string };
  investArray?: unknown;
  enabled?: boolean;
}) {
  const { data: portfolioData } = useBusinessPortfolioEstimate(businessId);

  return useQuery({
    queryKey: [
      "businessAvailableAssets",
      businessId,
      isSend,
      isEightyeightPay,
      balanceParams,
      balanceParamsEightEight,
      portfolioData?.timestamp,
    ],
    enabled: enabled && Boolean(businessId) && Boolean(portfolioData),
    queryFn: async () => {
      if (!portfolioData?.assets) {
        return [];
      }
      let availableAmount: unknown[] = [];
      if (isSend) {
        if (balanceParams?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset: { currencyType?: string; type?: string }) =>
              asset.currencyType === balanceParams.type ||
              asset.type === balanceParams.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }
      if (isEightyeightPay) {
        if (balanceParamsEightEight?.type) {
          availableAmount = portfolioData.assets.filter(
            (asset: { currencyType?: string; type?: string }) =>
              asset.currencyType === balanceParamsEightEight.type ||
              asset.type === balanceParamsEightEight.type
          );
        } else {
          availableAmount = portfolioData.assets;
        }
        return availableAmount.length > 0 ? availableAmount : [];
      }
      if (!isSend && !isEightyeightPay) {
        availableAmount = portfolioData.assets;
        return availableAmount.length > 0 ? availableAmount : [];
      }
      return [];
    },
    staleTime: 1000 * 60 * 2,
  });
}

export function useBusinessWalletBalance(businessId: string | null) {
  return useBusinessPortfolioEstimate(businessId);
}

/**
 * Like `useAvailableBalances` for business portfolio rows.
 */
export function useBusinessAvailableBalances(businessId: string | null) {
  const { data: portfolioData } = useBusinessPortfolioEstimate(businessId);

  return useQuery({
    queryKey: [
      "businessAvailableBalances",
      businessId,
      portfolioData?.timestamp,
    ],
    enabled: Boolean(businessId) && Boolean(portfolioData),
    queryFn: async () => {
      const assets = portfolioData?.assets || [];
      return assets.map(
        (asset: Record<string, unknown> & { asset?: string }) => {
          const preformatted =
            (typeof asset.formattedAvailableAmount === "string" &&
              asset.formattedAvailableAmount) ||
            (typeof asset.forattedAvailableAmount === "string" &&
              asset.forattedAvailableAmount) ||
            "";
          return {
            ...asset,
            formattedAvailableAmount: preformatted || undefined,
            availableAmountFormatted:
              preformatted ||
              `${asset.availableAmount} ${(asset.asset || "").toUpperCase()}`,
            assetEstimatedPrice:
              typeof asset.assetEstimatedPrice === "string"
                ? asset.assetEstimatedPrice
                : String(asset.assetEstimatedPrice ?? "0"),
          };
        }
      );
    },
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

export function useBusinessWalletTransactions(
  businessId: string | null,
  params: Record<string, string | number | boolean | undefined> = {
    page: 1,
    limit: 10,
    sort: "date_desc",
  },
  options?: { enabled?: boolean }
) {
  const { isAuthenticated } = useAuth();
  const queryEnabled =
    options?.enabled !== undefined
      ? Boolean(options.enabled) && Boolean(businessId) && isAuthenticated
      : Boolean(businessId) && isAuthenticated;

  return useQuery({
    queryKey: [
      ...(businessId ? businessWalletQueryKey(businessId) : []),
      "tx",
      params,
    ],
    queryFn: async () => {
      if (!businessId) {
        return {
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          limit: 10,
          results: [],
        };
      }
      const res = await getBusinessWalletTransactions(businessId, params);
      const data = unwrapBusinessResponse(res as { data?: unknown }) as
        | {
            totalItems?: number;
            totalPages?: number;
            currentPage?: number;
            limit?: number;
            results?: unknown[];
          }
        | null
        | undefined;
      const rawResults = Array.isArray(data?.results) ? data.results : [];
      const results = rawResults.map((row) =>
        normalizeBusinessWalletTransaction(row)
      );
      return {
        totalItems: data?.totalItems ?? 0,
        totalPages: data?.totalPages ?? 0,
        currentPage: data?.currentPage ?? 1,
        limit: data?.limit ?? 10,
        results,
      };
    },
    enabled: queryEnabled,
    staleTime: 1000 * 30,
  });
}

export function useBusinessPortfolioPerformance(
  businessId: string | null,
  params?: Record<string, string | number | boolean | undefined>
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: [
      ...(businessId ? businessWalletQueryKey(businessId) : []),
      "performance",
      params,
    ],
    queryFn: async () => {
      if (!businessId) {
        return {
          labels: [] as string[],
          values: [] as number[],
        };
      }
      const res = await getBusinessPortfolioPerformance(businessId, params);
      return extractBusinessPerformancePayload(res);
    },
    enabled: Boolean(businessId) && isAuthenticated,
    staleTime: 1000 * 60,
  });
}

export function useBusinessBanking(businessId: string | null) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: businessId
      ? [...businessWalletQueryKey(businessId), "banking"]
      : ["businessWallet", "none", "banking"],
    queryFn: async () => {
      if (!businessId) {
        return [];
      }
      const res = await getBusinessBanking(businessId);
      const unwrapped = unwrapBusinessResponse(res as { data?: unknown });
      return Array.isArray(unwrapped) ? unwrapped : [];
    },
    enabled: Boolean(businessId) && isAuthenticated,
    staleTime: 1000 * 30,
  });
}

export function useBusinessBank(
  businessId: string | null,
  bankAccId: string | null
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: businessId
      ? [...businessWalletQueryKey(businessId), "banking", "bank", bankAccId]
      : ["businessWallet", "none", "banking", "bank", bankAccId],
    queryFn: async () => {
      if (!businessId || !bankAccId) {
        return null;
      }
      const res = await getBusinessBank(businessId, { bankAccId });
      return unwrapBusinessResponse(res as { data?: unknown });
    },
    enabled: Boolean(businessId) && Boolean(bankAccId) && isAuthenticated,
    staleTime: 1000 * 30,
  });
}
